import { formatLocalDate } from 'utils/convertLocalFormat';
import * as htmlDocx from 'html-docx-js-typescript';

export interface LIAReportData {
  programName: string;
  trainingPeriod: string;
  moduleName: string;
  trainingDate: string;
  participantsCount: number;
  participantsRespondedCount: number;
  supervisorsRespondedCount: number;
  totalSupervisors: number;
  peersRespondedCount: number;
  totalPeers: number;
  subordinatesRespondedCount: number;
  totalSubordinates: number;
  externalRespondentsRespondedCount: number;
  totalExternalRespondents: number;
  surveyPeriod: string;
  learningObjectives: string;
  summaryDescription?: string;
  modules?: Array<{
    moduleID: string;
    moduleName?: string;
    learningObjectives?: string;
  }>;
  moduleNames?: string[];
  questionAverages: Array<{
    questionID: string;
    questionTitle: string;
    averageScore: number;
    responseCount: number;
    category?: string;
    sequence?: number;
    respondentType?: string;
    respondentTypeAverages: { [key: string]: { average: number; count: number } };
    answerDistribution: { [key: string]: { [answer: string]: number } };
    rangeItems?: Array<{ label: string; value: string }>;
  }>;
  categoryAverages?: Array<{
    category: string;
    averageScore: number;
    responseCount: number;
    respondentTypeAverages: { [key: string]: { average: number; count: number } };
  }>;
  businessUnits: Array<{
    name: string;
    participantsCount: number;
    supervisorsCount: number;
  }>;
  conclusionStatements?: Array<{
    title: string;
    percentage: number;
    value: number;
  }>;
  conclusions: {
    capability: {
      participants: number;
      supervisors: number;
      statement: string;
    };
    improvement: {
      participants: number;
      supervisors: number;
      statement: string;
    };
    awareness: {
      participants: number;
      supervisors: number;
      statement: string;
    };
    performance: {
      participants: number;
      supervisors: number;
    };
    bestPractice: {
      participants: number;
      supervisors: number;
    };
  };
  preTraining: {
    condition: string;
    reason: string;
    desiredSkills: string;
    expectations: string;
  };
  postTraining: {
    challenges: string;
    challengeStatistics: {
      statement: string;
      percentage: number;
    }[];
    changedPercentage: number;
    additionalConclusions: string[];
  };
  charts?: any;
}

// Helper function to count unique respondents who answered
function countRespondedParticipantsByType(
  respondentAnswers: any[],
  respondentType?: string
): number {
  const uniqueRespondents = new Set();

  respondentAnswers.forEach((answer) => {
    if (!respondentType || answer.respondentType === respondentType) {
      // Use binusianID if available, otherwise use email or externalEmail
      const identifier = answer.binusianID || answer.email || answer.externalEmail;
      if (identifier) {
        uniqueRespondents.add(identifier);
      }
    }
  });

  return uniqueRespondents.size;
}

// Helper function to count total respondents by type
function countTotalRespondentsByType(respondents: any[], respondentType: string): number {
  return respondents.filter((respondent) => respondent.respondentType === respondentType).length;
}

// Updated function to calculate question averages grouped by questionTitle
function calculateQuestionAveragesGrouped(respondentAnswers: any[]): Array<{
  questionID: string;
  questionTitle: string;
  averageScore: number;
  responseCount: number;
  category?: string;
  sequence?: number;
  respondentType?: string;
  respondentTypeAverages: { [key: string]: { average: number; count: number } };
  answerDistribution: { [key: string]: { [answer: string]: number } }; // For pie charts
  rangeItems?: Array<{ label: string; value: string }>;
}> {
  const questionMap = new Map();

  // Group answers by questionTitle (so same questions across roles are grouped together)
  respondentAnswers.forEach((answer) => {
    if (answer.questionType === 'Range' && answer.answer !== null && answer.answer !== undefined) {
      const questionKey = answer.questionTitle; // Use questionTitle as the grouping key
      const score = parseFloat(answer.answer);
      const respondentType = answer.respondentType || 'Participant';
      const answerValue = answer.answer.toString();

      // Skip invalid scores
      if (isNaN(score)) return;

      if (!questionMap.has(questionKey)) {
        questionMap.set(questionKey, {
          questionID: answer.questionID, // Keep first questionID for reference
          questionTitle: answer.questionTitle,
          questionType: answer.questionType,
          category: answer.category || '',
          sequence: answer.sequence || answer.questionSequence || 0,
          scores: [],
          respondentTypeScores: {},
          answerDistribution: {},
          allRespondentTypes: new Set(),
          rangeItems: answer.rangeItems || [],
          answerLabelMap: new Map() // Store mapping of answer value to answerLabel
        });
      }

      const questionData = questionMap.get(questionKey);
      questionData.scores.push(score);
      questionData.allRespondentTypes.add(respondentType);

      // Store answerLabel mapping if available
      if (answer.answerLabel && !questionData.answerLabelMap.has(answerValue)) {
        questionData.answerLabelMap.set(answerValue, answer.answerLabel);
      }

      // Group by respondent type
      if (!questionData.respondentTypeScores[respondentType]) {
        questionData.respondentTypeScores[respondentType] = [];
      }
      questionData.respondentTypeScores[respondentType].push(score);

      // Track answer distribution for each respondent type
      if (!questionData.answerDistribution[respondentType]) {
        questionData.answerDistribution[respondentType] = {};
      }
      if (!questionData.answerDistribution[respondentType][answerValue]) {
        questionData.answerDistribution[respondentType][answerValue] = 0;
      }
      questionData.answerDistribution[respondentType][answerValue]++;
    }
  });

  // Calculate averages
  const averages: Array<{
    questionID: string;
    questionTitle: string;
    averageScore: number;
    responseCount: number;
    category?: string;
    sequence?: number;
    respondentType?: string;
    respondentTypeAverages: { [key: string]: { average: number; count: number } };
    answerDistribution: { [key: string]: { [answer: string]: number } };
    rangeItems?: Array<{ label: string; value: string }>;
  }> = [];

  questionMap.forEach((data, questionTitle) => {
    if (data.scores.length > 0) {
      const sum = data.scores.reduce((a: number, b: number) => a + b, 0);
      const average = sum / data.scores.length;

      // Calculate averages by respondent type
      const respondentTypeAverages: { [key: string]: { average: number; count: number } } = {};

      Object.keys(data.respondentTypeScores).forEach((respondentType) => {
        const scores = data.respondentTypeScores[respondentType];
        if (scores.length > 0) {
          const typeSum = scores.reduce((a: number, b: number) => a + b, 0);
          respondentTypeAverages[respondentType] = {
            average: typeSum / scores.length,
            count: scores.length
          };
        }
      });

      // Build rangeItems from answerLabelMap if available, otherwise use existing rangeItems
      let finalRangeItems = data.rangeItems;
      if (data.answerLabelMap.size > 0) {
        finalRangeItems = Array.from(data.answerLabelMap, ([value, label]) => ({
          value: value,
          label: label
        }));
      }

      averages.push({
        questionID: data.questionID, // Use the stored questionID from data
        questionTitle: questionTitle, // Use the key which is questionTitle
        averageScore: average,
        responseCount: data.scores.length,
        category: data.category,
        sequence: data.sequence,
        respondentType:
          data.allRespondentTypes.size > 1
            ? 'Multiple'
            : (Array.from(data.allRespondentTypes)[0] as string),
        respondentTypeAverages,
        answerDistribution: data.answerDistribution,
        rangeItems: finalRangeItems
      });
    }
  });

  // Sort by sequence primarily (to keep questions in order)
  return averages.sort((a, b) => {
    // Sort by sequence first
    return (a.sequence || 0) - (b.sequence || 0);
  });
}

// Helper function to calculate business unit distribution
function calculateBusinessUnitDistribution(
  participants: any[],
  respondents: any[]
): Array<{
  name: string;
  participantsCount: number;
  supervisorsCount: number;
}> {
  const divisionMap = new Map();

  // Process participants to count unique divisions
  participants.forEach((participant) => {
    const division = participant.division || 'Unknown Division';
    const binusianID = participant.binusianID;

    if (!divisionMap.has(division)) {
      divisionMap.set(division, {
        participantIDs: new Set(),
        supervisorIDs: new Set()
      });
    }

    const divisionData = divisionMap.get(division);
    divisionData.participantIDs.add(binusianID);
  });

  // Process respondents to count unique supervisors by division
  respondents.forEach((respondent) => {
    const division = respondent.division || 'Unknown Division';
    const binusianID = respondent.binusianID;
    const respondentType = respondent.respondentType;

    if (!divisionMap.has(division)) {
      divisionMap.set(division, {
        participantIDs: new Set(),
        supervisorIDs: new Set()
      });
    }

    const divisionData = divisionMap.get(division);

    // Count unique supervisors based on respondent type
    if (respondentType === 'Supervisor') {
      divisionData.supervisorIDs.add(binusianID);
    }
  });

  // Convert to array format
  const businessUnits: Array<{
    name: string;
    participantsCount: number;
    supervisorsCount: number;
  }> = [];

  divisionMap.forEach((data, division) => {
    businessUnits.push({
      name: division,
      participantsCount: data.participantIDs.size,
      supervisorsCount: data.supervisorIDs.size
    });
  });

  // Sort by name for consistent ordering
  return businessUnits.sort((a, b) => a.name.localeCompare(b.name));
}

// Helper function to calculate category-based averages
function calculateCategoryAverages(respondentAnswers: any[]): Array<{
  category: string;
  averageScore: number;
  responseCount: number;
  respondentTypeAverages: { [key: string]: { average: number; count: number } };
}> {
  const categoryMap = new Map();

  // Group answers by category
  respondentAnswers.forEach((answer) => {
    if (
      answer.questionType === 'Range' &&
      answer.answer !== null &&
      answer.answer !== undefined &&
      answer.category
    ) {
      const categories = answer.category.split(',').map((cat: string) => cat.trim());
      const score = parseFloat(answer.answer);
      const respondentType = answer.respondentType || 'Participant';
      const respondentIdentifier =
        answer.binusianID ||
        answer.email ||
        answer.externalEmail ||
        answer.trAssessmentRecordID ||
        answer.respondentID ||
        answer.participantID ||
        answer.answerId;

      // Skip invalid scores
      if (isNaN(score)) return;

      categories.forEach((category: string) => {
        if (!category) return;

        if (!categoryMap.has(category)) {
          categoryMap.set(category, {
            scores: [],
            respondentTypeScores: {},
            respondentTypeIdentifiers: {}
          });
        }

        const categoryData = categoryMap.get(category);
        categoryData.scores.push(score);

        // Group by respondent type
        if (!categoryData.respondentTypeScores[respondentType]) {
          categoryData.respondentTypeScores[respondentType] = [];
          categoryData.respondentTypeIdentifiers[respondentType] = new Set<string>();
        }
        categoryData.respondentTypeScores[respondentType].push(score);
        if (respondentIdentifier) {
          categoryData.respondentTypeIdentifiers[respondentType].add(
            String(respondentIdentifier).trim().toLowerCase()
          );
        }
      });
    }
  });

  // Calculate averages
  const averages: Array<{
    category: string;
    averageScore: number;
    responseCount: number;
    respondentTypeAverages: { [key: string]: { average: number; count: number } };
  }> = [];

  categoryMap.forEach((data, category) => {
    if (data.scores.length > 0) {
      const sum = data.scores.reduce((a: number, b: number) => a + b, 0);
      const average = sum / data.scores.length;

      // Calculate averages by respondent type
      const respondentTypeAverages: { [key: string]: { average: number; count: number } } = {};

      Object.keys(data.respondentTypeScores).forEach((respondentType) => {
        const scores = data.respondentTypeScores[respondentType];
        if (scores.length > 0) {
          const typeSum = scores.reduce((a: number, b: number) => a + b, 0);
          respondentTypeAverages[respondentType] = {
            average: typeSum / scores.length,
            count: data.respondentTypeIdentifiers[respondentType].size
          };
        }
      });

      averages.push({
        category,
        averageScore: average,
        responseCount: data.scores.length,
        respondentTypeAverages
      });
    }
  });

  // Sort by category name for consistent ordering
  return averages.sort((a, b) => a.category.localeCompare(b.category));
}

function calculateMaxRangeResponses(assessment: any): any[] {
  const maxResponses: any[] = [];

  if (!assessment || !assessment.modules) return maxResponses;

  assessment.modules.forEach((module: any) => {
    if (!module.questions) return;

    const rangeQuestions = module.questions.filter((q: any) => q.type === 'Range');

    rangeQuestions.forEach((question: any) => {
      // Initialize counters for each possible answer
      const answerCounts: { [key: string]: number } = {};
      let totalResponses = 0;

      // Count responses from participants
      if (assessment.participants) {
        assessment.participants.forEach((participant: any) => {
          if (!participant.modules) return;

          participant.modules.forEach((pModule: any) => {
            if (pModule.moduleID !== module.moduleID || !pModule.questions) return;

            pModule.questions.forEach((pQuestion: any) => {
              if (
                pQuestion.trAssessmentQuestionID === question.trAssessmentQuestionID &&
                pQuestion.answer !== null
              ) {
                const answer = pQuestion.answer;
                answerCounts[answer] = (answerCounts[answer] || 0) + 1;
                totalResponses++;
              }
            });
          });
        });
      }

      // Count responses from respondents
      if (assessment.respondents) {
        assessment.respondents.forEach((respondent: any) => {
          if (!respondent.responses) return;

          respondent.responses.forEach((response: any) => {
            if (!response.modules) return;

            response.modules.forEach((rModule: any) => {
              if (rModule.moduleID !== module.moduleID || !rModule.questions) return;

              rModule.questions.forEach((rQuestion: any) => {
                if (
                  rQuestion.trAssessmentQuestionID === question.trAssessmentQuestionID &&
                  rQuestion.answer !== null
                ) {
                  const answer = rQuestion.answer;
                  answerCounts[answer] = (answerCounts[answer] || 0) + 1;
                  totalResponses++;
                }
              });
            });
          });
        });
      }

      // Find the answer with the highest count
      let maxAnswer = '';
      let maxCount = 0;

      Object.keys(answerCounts).forEach((answer) => {
        if (answerCounts[answer] > maxCount) {
          maxCount = answerCounts[answer];
          maxAnswer = answer;
        }
      });

      // Calculate percentage
      const percentage = totalResponses > 0 ? Math.round((maxCount / totalResponses) * 100) : 0;

      // Add to results
      maxResponses.push({
        questionID: question.trAssessmentQuestionID,
        questionTitle: question.title,
        maxAnswer,
        maxValue: Number(maxAnswer),
        percentage,
        totalResponses
      });
    });
  });

  // Sort by percentage (highest first)
  return maxResponses.sort((a, b) => b.percentage - a.percentage);
}

function calculateAgreementPercentage(
  assessment: any,
  category: string,
  respondentType: string
): number {
  // For simplicity, return default values based on the template
  if (category === 'capability') {
    return respondentType === 'participants' ? 70 : 65;
  } else if (category === 'improvement') {
    return respondentType === 'participants' ? 75 : 70;
  } else if (category === 'awareness') {
    return respondentType === 'participants' ? 68 : 62;
  } else if (category === 'performance') {
    return respondentType === 'participants' ? 65 : 50;
  } else if (category === 'bestPractice') {
    return respondentType === 'participants' ? 70 : 60;
  }
  return 65; // Default value
}

/* eslint-disable */
function extractCapabilityStatement(assessment: any): string {
  return 'melakukan analisis data dengan metode yang dipelajari';
}

function extractImprovementStatement(assessment: any): string {
  return 'kemampuan pengambilan keputusan berbasis data';
}

function extractAwarenessStatement(assessment: any): string {
  return 'perlunya pendekatan analitis dalam menyelesaikan masalah';
}

function extractPreTrainingData(assessment: any, dataType: string): string {
  // Simple function that returns the same value regardless of assessment
  switch (dataType) {
    case 'condition':
      return 'Kesulitan dalam menganalisis data yang kompleks';
    case 'reason':
      return 'Kebutuhan untuk membuat keputusan berbasis data yang lebih baik';
    case 'skills':
      return 'Analisis data, visualisasi data, dan interpretasi hasil';
    case 'expectations':
      return 'Mampu mengaplikasikan teknik analisis data dalam pekerjaan sehari-hari';
    default:
      return '';
  }
}

function extractPostTrainingData(assessment: any, dataType: string): string {
  switch (dataType) {
    case 'challenges':
      return 'Implementasi metode analisis data yang dipelajari';
    default:
      return '';
  }
}

function extractChallengeStatistics(assessment: any): any[] {
  return [
    { statement: 'kekurangan waktu untuk menerapkan metode yang dipelajari', percentage: 35 },
    { statement: 'kesulitan mengakses data yang dibutuhkan', percentage: 25 }
  ];
}

function extractAdditionalConclusions(assessment: any): string[] {
  return [
    '85% peserta merasa lebih percaya diri dalam menganalisis data',
    '76% peserta telah menerapkan minimal satu teknik yang dipelajari dalam pekerjaan mereka'
  ];
}
/* eslint-enable */

export const prepareLIAReportData = (
  exportData: any,
  summaryDescription?: string
): LIAReportData => {
  const assessmentDetails = exportData.assessmentDetails || {};
  const answerData = exportData.answerData || {};

  // Combine all answer sources
  const participantAnswers = answerData.participantAnswers || [];
  const respondentAnswers = answerData.respondentAnswers || [];
  const externalAnswers = answerData.externalAnswers || [];

  // Map participant answers to have proper respondentType
  const mappedParticipantAnswers = participantAnswers.map((answer: any) => ({
    ...answer,
    respondentType: 'Participant' // Force Participant type for self-assessments
  }));

  // Map external answers to have proper respondentType
  const mappedExternalAnswers = externalAnswers.map((answer: any) => ({
    ...answer,
    respondentType: answer.respondentType || 'External Respondent' // Use their respondentType or default to External
  }));

  // Combine all answers for processing
  const allAnswers = [...mappedParticipantAnswers, ...respondentAnswers, ...mappedExternalAnswers];

  const respondents = exportData.respondents || [];
  const participants = exportData.participants || [];

  // 1. Count total participants
  const totalParticipants = participants.length;

  // 2. Count unique participants who answered
  const participantsRespondedCount = countRespondedParticipantsByType(
    mappedParticipantAnswers,
    'Participant'
  );

  // 3. Count supervisors who answered and total supervisors
  const supervisorsRespondedCount = countRespondedParticipantsByType(allAnswers, 'Supervisor');
  const totalSupervisors = countTotalRespondentsByType(respondents, 'Supervisor');

  // 4. Count peers who answered and total peers
  const peersRespondedCount = countRespondedParticipantsByType(allAnswers, 'Peer');
  const totalPeers = countTotalRespondentsByType(respondents, 'Peer');

  // 5. Count subordinates who answered and total subordinates
  const subordinatesRespondedCount = countRespondedParticipantsByType(allAnswers, 'Subordinate');
  const totalSubordinates = countTotalRespondentsByType(respondents, 'Subordinate');

  // 6. Count external respondents who answered and total external respondents
  const externalRespondentsRespondedCount = countRespondedParticipantsByType(
    allAnswers,
    'External Respondent'
  );
  const externalRespondents = exportData.externalRespondents || [];
  const totalExternalRespondents = externalRespondents.length;

  // 7. Calculate question averages grouped by questionID - NOW WITH ALL ANSWERS
  const questionAverages = calculateQuestionAveragesGrouped(allAnswers);

  // Calculate business unit distribution from participants and respondents
  const businessUnits = calculateBusinessUnitDistribution(participants, respondents);

  // Calculate category-based averages from all answers
  const categoryAverages = calculateCategoryAverages(allAnswers);

  const modules =
    exportData.modules?.map((module: any) => ({
      moduleID: module.moduleID,
      moduleName: module.moduleName || module.moduleID,
      learningObjectives: module.learningObjectives || null
    })) || [];

  const moduleNames = modules.map((module: any) => module.moduleName || module.moduleID);

  const maxRangeResponses = calculateMaxRangeResponses(exportData);
  const conclusionStatements = maxRangeResponses.map((response) => {
    return {
      title: response.questionTitle,
      percentage: response.percentage,
      value: response.maxValue
    };
  });

  const reportData: LIAReportData = {
    programName:
      assessmentDetails.programName || assessmentDetails.name || 'AI Month for Employee 2024',
    trainingPeriod:
      assessmentDetails.startTime && assessmentDetails.endTime
        ? `${formatLocalDate(assessmentDetails.startTime, 'DD/MM/YYYY')} - ${formatLocalDate(assessmentDetails.endTime, 'DD/MM/YYYY')}`
        : 'April 2024',
    moduleName:
      moduleNames.join(', ') ||
      assessmentDetails.name ||
      'Artificial Intelligence (AI) for Operational Purposes',
    trainingDate: assessmentDetails.startTime
      ? formatLocalDate(assessmentDetails.startTime, 'DD/MM/YYYY')
      : '23 -- 30 April 2024',
    summaryDescription: summaryDescription || undefined,
    participantsCount: totalParticipants, // Count actual participants
    participantsRespondedCount: participantsRespondedCount,
    supervisorsRespondedCount: supervisorsRespondedCount,
    totalSupervisors: totalSupervisors,
    peersRespondedCount: peersRespondedCount,
    totalPeers: totalPeers,
    subordinatesRespondedCount: subordinatesRespondedCount,
    totalSubordinates: totalSubordinates,
    externalRespondentsRespondedCount: externalRespondentsRespondedCount,
    totalExternalRespondents: totalExternalRespondents,
    surveyPeriod:
      assessmentDetails.startTime && assessmentDetails.endTime
        ? `${formatLocalDate(assessmentDetails.startTime, 'DD/MM/YYYY')} - ${formatLocalDate(assessmentDetails.endTime, 'DD/MM/YYYY')}`
        : 'April 2024',
    learningObjectives: assessmentDetails.learningObjectives || '-',
    modules,
    moduleNames,
    questionAverages,
    businessUnits,
    categoryAverages,
    conclusionStatements,
    conclusions: {
      capability: {
        participants: calculateAgreementPercentage(exportData, 'capability', 'participants'),
        supervisors: calculateAgreementPercentage(exportData, 'capability', 'supervisors'),
        statement: extractCapabilityStatement(exportData)
      },
      improvement: {
        participants: calculateAgreementPercentage(exportData, 'improvement', 'participants'),
        supervisors: calculateAgreementPercentage(exportData, 'improvement', 'supervisors'),
        statement: extractImprovementStatement(exportData)
      },
      awareness: {
        participants: calculateAgreementPercentage(exportData, 'awareness', 'participants'),
        supervisors: calculateAgreementPercentage(exportData, 'awareness', 'supervisors'),
        statement: extractAwarenessStatement(exportData)
      },
      performance: {
        participants: 65,
        supervisors: 50
      },
      bestPractice: {
        participants: 70,
        supervisors: 60
      }
    },
    preTraining: {
      condition: extractPreTrainingData(exportData, 'condition'),
      reason: extractPreTrainingData(exportData, 'reason'),
      desiredSkills: extractPreTrainingData(exportData, 'skills'),
      expectations: extractPreTrainingData(exportData, 'expectations')
    },
    postTraining: {
      challenges: extractPostTrainingData(exportData, 'challenges'),
      challengeStatistics: extractChallengeStatistics(exportData),
      changedPercentage: 85,
      additionalConclusions: extractAdditionalConclusions(exportData)
    }
  };

  return reportData;
};

// Helper function to detect available respondent types in the data
function getAvailableRespondentTypes(data: LIAReportData): string[] {
  const availableTypes = new Set<string>();

  // Check questionAverages for respondent types with data
  data.questionAverages?.forEach((qa) => {
    Object.keys(qa.respondentTypeAverages).forEach((type) => {
      if (qa.respondentTypeAverages[type].count > 0) {
        availableTypes.add(type);
      }
    });
  });

  // Order types: Participant, Supervisor, Peer, Subordinate, External Respondent
  const orderedTypes = ['Participant', 'Supervisor', 'Peer', 'Subordinate', 'External Respondent'];

  return orderedTypes.filter((type) => availableTypes.has(type));
}

// Helper function to format respondent type names for Indonesian text
function formatRespondentTypesIndonesian(types: string[]): string {
  const indonesianNames: { [key: string]: string } = {
    Participant: 'peserta',
    Supervisor: 'supervisor',
    Peer: 'peer',
    Subordinate: 'subordinate',
    'External Respondent': 'external respondent'
  };

  const translatedTypes = types.map((type) => indonesianNames[type] || type.toLowerCase());

  if (translatedTypes.length === 0) return '';
  if (translatedTypes.length === 1) return translatedTypes[0];
  if (translatedTypes.length === 2) return translatedTypes.join(' dan ');

  const lastType = translatedTypes.pop();
  return translatedTypes.join(', ') + ', dan ' + lastType;
}

export const generateLIAReportTemplate = (data: LIAReportData): string => {
  // Get available respondent types from the data
  const availableTypes = getAvailableRespondentTypes(data);

  // Calculate total averages per role for the footer
  const calculateTotalAvgPerRole = () => {
    const totals: { [key: string]: { sum: number; count: number } } = {};

    // Initialize totals for each available type
    availableTypes.forEach((type) => {
      totals[type] = { sum: 0, count: 0 };
    });

    // Sum up all averages per role
    data.questionAverages?.forEach((qa) => {
      availableTypes.forEach((type) => {
        const avg = qa.respondentTypeAverages[type]?.average;
        if (avg !== undefined && avg !== null && !isNaN(avg)) {
          totals[type].sum += avg;
          totals[type].count += 1;
        }
      });
    });

    // Calculate average of averages
    const avgPerRole: { [key: string]: string } = {};
    availableTypes.forEach((type) => {
      if (totals[type].count > 0) {
        avgPerRole[type] = (totals[type].sum / totals[type].count).toFixed(2);
      } else {
        avgPerRole[type] = '-';
      }
    });

    return avgPerRole;
  };

  const totalAvgPerRole = calculateTotalAvgPerRole();

  // Generate dynamic question averages table based on available respondent types
  const questionAveragesRows =
    data.questionAverages
      ?.map((qa) => {
        const cells = availableTypes
          .map((type) => {
            const avg = qa.respondentTypeAverages[type]?.average?.toFixed(2) || '-';
            return `<td><strong>${avg}</strong></td>`;
          })
          .join('');

        return `
        <tr>
          <td>${qa.questionTitle}</td>
          ${cells}
          <td><strong>${qa.averageScore.toFixed(2)}</strong></td>
        </tr>
      `;
      })
      .join('') ||
    `
    <tr>
      <td>No question data available</td>
      ${availableTypes.map(() => '<td><strong>-</strong></td>').join('')}
      <td><strong>-</strong></td>
    </tr>
  `;

  // Generate footer row with total averages per role
  const footerCells = availableTypes
    .map(
      (type) =>
        `<td style="background-color: #e8e8e8;"><strong>${totalAvgPerRole[type]}</strong></td>`
    )
    .join('');

  // Calculate overall total average
  const overallTotal = availableTypes.reduce((sum, type) => {
    const val = parseFloat(totalAvgPerRole[type]);
    return sum + (isNaN(val) ? 0 : val);
  }, 0);
  const overallAvg =
    availableTypes.length > 0
      ? (
          overallTotal / availableTypes.filter((type) => totalAvgPerRole[type] !== '-').length
        ).toFixed(2)
      : '-';

  const questionAveragesFooter = `
    <tr style="background-color: #e8e8e8; font-weight: bold;">
      <td><strong></strong></td>
      ${footerCells}
      <td style="background-color: #e8e8e8;"><strong>${overallAvg}</strong></td>
    </tr>
  `;

  // Generate dynamic table headers for question averages table
  const questionAveragesHeaders = availableTypes
    .map((type) => `<td><strong>${type}</strong></td>`)
    .join('');

  // Generate dynamic description text
  const respondentTypesText = formatRespondentTypesIndonesian(availableTypes);
  const averageDescriptionText = `Berikut ini adalah nilai rata-rata dari ${respondentTypesText} mengenai dampak pelatihan pada performa kinerja:`;

  // Helper function to generate Word-friendly bar chart using table with colored cells
  const generateWordFriendlyBarChart = (
    distribution: { [answer: string]: number },
    respondentType: string,
    _maxValue: number = 6,
    rangeItems?: Array<{ label: string; value: string }>
  ) => {
    const total = Object.values(distribution).reduce((a, b) => a + b, 0);
    if (total === 0) return '<p><em>No data available</em></p>';

    const sortedData = Object.entries(distribution).sort(
      (a, b) => parseFloat(b[0]) - parseFloat(a[0])
    );

    // Create a mapping from value to label
    const valueToLabel: { [key: string]: string } = {};
    if (rangeItems && rangeItems.length > 0) {
      rangeItems.forEach((item) => {
        valueToLabel[item.value] = item.label;
      });
    }

    // Color scheme for different respondent types
    const colors: { [key: string]: string } = {
      Participant: '#007BFF',
      Supervisor: '#6F42C1',
      Peer: '#FD7E14',
      Subordinate: '#28A745',
      'External Respondent': '#E83E8C'
    };
    const barColor = colors[respondentType] || '#007BFF';

    let html = `
      <table border="1" cellpadding="6" cellspacing="0" style="width: 100%; margin: 15px 0; border-collapse: collapse; font-size: 11px;">
        <thead>
          <tr style="background-color: #f0f0f0;">
            <th style="text-align: center; width: 12%; border: 1px solid #000;">Jawaban</th>
            <th style="text-align: left; width: 58%; border: 1px solid #000;">Visual Distribution (${respondentType})</th>
            <th style="text-align: center; width: 15%; border: 1px solid #000;">Jumlah</th>
            <th style="text-align: center; width: 15%; border: 1px solid #000;">Persentase</th>
          </tr>
        </thead>
        <tbody>
    `;

    sortedData.forEach(([answer, count]) => {
      const percentage = ((count / total) * 100).toFixed(1);
      const barPercentage = (count / total) * 100;

      // Format answer with label if available
      const answerDisplay = valueToLabel[answer] ? `${valueToLabel[answer]} (${answer})` : answer;

      // Create visual bar using table cells with background color
      html += `
        <tr>
          <td style="font-weight: bold; text-align: center; border: 1px solid #000; background-color: #f9f9f9;">${answerDisplay}</td>
          <td style="padding: 0; border: 1px solid #000;">
            <table cellpadding="0" cellspacing="0" style="width: 100%; border: none;">
              <tr>
                <td style="width: ${barPercentage}%; background-color: ${barColor}; height: 20px; border: none; color: white; font-size: 10px; font-weight: bold; text-align: right; padding-right: 4px;">
                  ${barPercentage > 15 ? percentage + '%' : ''}
                </td>
                <td style="width: ${100 - barPercentage}%; background-color: #f0f0f0; height: 20px; border: none;">
                  ${barPercentage <= 15 ? '<span style="padding-left: 4px; font-size: 10px; color: #666;">' + percentage + '%</span>' : ''}
                </td>
              </tr>
            </table>
          </td>
          <td style="text-align: center; border: 1px solid #000;">${count}</td>
          <td style="text-align: center; border: 1px solid #000; font-weight: bold;">${percentage}%</td>
        </tr>
      `;
    });

    html += `
          <tr style="background-color: #f5f5f5; font-weight: bold;">
            <td style="border: 1px solid #000;">Total</td>
            <td style="border: 1px solid #000;"></td>
            <td style="text-align: center; border: 1px solid #000;">${total}</td>
            <td style="text-align: center; border: 1px solid #000;">100%</td>
          </tr>
        </tbody>
      </table>
    `;

    return html;
  };

  // Helper function to generate combined chart for multi-role questions
  const generateCombinedBarChart = (
    distributions: { [respondentType: string]: { [answer: string]: number } },
    rangeItems?: Array<{ label: string; value: string }>
  ) => {
    // Create a mapping from value to label
    const valueToLabel: { [key: string]: string } = {};
    if (rangeItems && rangeItems.length > 0) {
      rangeItems.forEach((item) => {
        valueToLabel[item.value] = item.label;
      });
    }

    // Get all unique answers across all respondent types
    const allAnswers = new Set<string>();
    Object.values(distributions).forEach((dist) => {
      Object.keys(dist).forEach((answer) => allAnswers.add(answer));
    });

    // Sort answers numerically
    const sortedAnswers = Array.from(allAnswers).sort((a, b) => parseFloat(b) - parseFloat(a));

    // Color scheme for different respondent types
    const colors: { [key: string]: string } = {
      Participant: '#007BFF',
      Supervisor: '#6F42C1',
      Peer: '#FD7E14',
      Subordinate: '#28A745',
      'External Respondent': '#E83E8C'
    };

    const respondentTypes = Object.keys(distributions);

    let html = `
      <table border="1" cellpadding="8" cellspacing="0" style="width: 100%; margin: 15px 0; border-collapse: collapse; font-size: 11px;">
        <thead>
          <tr style="background-color: #f0f0f0;">
            <th style="text-align: center; width: 15%; border: 1px solid #000;">Answer</th>
            <th style="text-align: center; width: 15%; border: 1px solid #000;">Role</th>
            <th style="text-align: left; width: 40%; border: 1px solid #000;">Visual Distribution</th>
            <th style="text-align: center; width: 15%; border: 1px solid #000;">Count</th>
            <th style="text-align: center; width: 15%; border: 1px solid #000;">Percentage</th>
          </tr>
        </thead>
        <tbody>
    `;

    sortedAnswers.forEach((answer) => {
      const answerDisplay = valueToLabel[answer] ? `${valueToLabel[answer]} (${answer})` : answer;
      let firstRow = true;

      respondentTypes.forEach((respondentType) => {
        const distribution = distributions[respondentType];
        const count = distribution[answer] || 0;
        const total = Object.values(distribution).reduce((a, b) => a + b, 0);
        const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
        const barPercentage = total > 0 ? (count / total) * 100 : 0;
        const barColor = colors[respondentType] || '#7B2183';

        html += `
          <tr>
            ${firstRow ? `<td rowspan="${respondentTypes.length}" style="font-weight: bold; text-align: center; border: 1px solid #000; background-color: #f9f9f9; vertical-align: middle;">${answerDisplay}</td>` : ''}
            <td style="text-align: center; border: 1px solid #000; background-color: ${barColor}; color: white; font-weight: bold;">${respondentType}</td>
            <td style="padding: 0; border: 1px solid #000;">
              <table cellpadding="0" cellspacing="0" style="width: 100%; border: none;">
                <tr>
                  <td style="width: ${barPercentage}%; background-color: ${barColor}; height: 20px; border: none; color: white; font-size: 10px; font-weight: bold; text-align: right; padding-right: 4px;">
                    ${barPercentage > 15 ? percentage + '%' : ''}
                  </td>
                  <td style="width: ${100 - barPercentage}%; background-color: #f0f0f0; height: 20px; border: none;">
                    ${barPercentage <= 15 ? '<span style="padding-left: 4px; font-size: 10px; color: #666;">' + percentage + '%</span>' : ''}
                  </td>
                </tr>
              </table>
            </td>
            <td style="text-align: center; border: 1px solid #000;">${count}</td>
            <td style="text-align: center; border: 1px solid #000; font-weight: bold;">${percentage}%</td>
          </tr>
        `;
        firstRow = false;
      });
    });

    html += `
      </tr>
        </tbody>
      </table>
    `;

    return html;
  };

  // Generate individual charts for each question - SEPARATED BY RESPONDENT TYPE
  const generateQuestionChartsByType = () => {
    if (!data.questionAverages || data.questionAverages.length === 0) {
      return '<p>No question data available</p>';
    }

    // Separate questions into single-role and multi-role
    const multiRoleQuestions = data.questionAverages.filter(
      (qa) => Object.keys(qa.respondentTypeAverages).length > 1
    );
    const singleRoleQuestions = data.questionAverages.filter(
      (qa) => Object.keys(qa.respondentTypeAverages).length === 1
    );

    let html = '';

    // First, show single-role questions grouped by respondent type
    const respondentTypes = [
      'Participant',
      'Supervisor',
      'Peer',
      'Subordinate',
      'External Respondent'
    ];

    respondentTypes.forEach((respondentType) => {
      // Check if this respondent type has any single-role questions
      const typeQuestions = singleRoleQuestions.filter(
        (qa) => qa.respondentTypeAverages[respondentType]
      );

      if (typeQuestions.length === 0) return;

      html += `
        <div class="respondent-type-section" style="margin-bottom: 40px; page-break-inside: avoid;">
          <h4 style="color: #7B2183; border-bottom: 2px solid #7B2183; padding-bottom: 10px; margin-bottom: 20px;">
            ${respondentType} Responses
          </h4>
      `;

      typeQuestions.forEach((qa, index) => {
        const typeData = qa.respondentTypeAverages[respondentType];
        const distribution = qa.answerDistribution[respondentType];

        if (!typeData || !distribution) return;

        html += `
          <div class="question-chart" style="margin-bottom: 30px; padding: 20px; background: #f9f9f9; border-radius: 8px; border: 1px solid #e0e0e0;">
            <h5 style="margin: 0 0 15px 0; color: #333; font-size: 15px;">
              Question ${index + 1}: ${qa.questionTitle}
            </h5>
            <div style="display: flex; gap: 20px; margin-bottom: 15px;">
              <div style="flex: 1; background: white; padding: 15px; border-radius: 6px; border: 1px solid #ddd;">
                <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Average Score</div>
                <div style="font-size: 24px; font-weight: bold; color: #7B2183;">${typeData.average.toFixed(2)}</div>
              </div>
              <div style="flex: 1; background: white; padding: 15px; border-radius: 6px; border: 1px solid #ddd;">
                <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Total Responses</div>
                <div style="font-size: 24px; font-weight: bold; color: #333;">${typeData.count}</div>
              </div>
            </div>
            ${generateWordFriendlyBarChart(distribution, respondentType, 6, qa.rangeItems)}
          </div>
        `;
      });

      html += '</div>';
    });

    // Then, show multi-role questions in a separate section with combined charts
    if (multiRoleQuestions.length > 0) {
      html += `
        <div class="multi-role-section" style="margin-bottom: 40px; page-break-before: always;">
          <h3 style="color: #7B2183; border-bottom: 3px solid #7B2183; padding-bottom: 12px; margin-bottom: 25px; font-size: 18px;">
            Analisa per Pertanyaan (Berbagai tipe)
          </h3>
          <p style="color: #666; font-style: italic; margin-bottom: 25px;">
            Berikut ini adalah analisa mendalam untuk setiap pertanyaan yang melibatkan berbagai tipe responden (dan participant). Grafik di bawah ini menampilkan distribusi jawaban yang dipisahkan berdasarkan
            tipe masing-masing responden (dan participant).
          </p>
      `;

      multiRoleQuestions.forEach((qa, index) => {
        const roleTypes = Object.keys(qa.respondentTypeAverages);

        // Calculate combined totals
        let totalResponses = 0;
        let weightedSum = 0;
        roleTypes.forEach((type) => {
          const typeData = qa.respondentTypeAverages[type];
          totalResponses += typeData.count;
          weightedSum += typeData.average * typeData.count;
        });
        const overallAverage = totalResponses > 0 ? weightedSum / totalResponses : 0;

        html += `
          <div class="question-chart multi-role" style="margin-bottom: 40px; padding: 25px; background: #f5f9ff; border-radius: 8px; border: 2px solid #7B2183;">
            <h5 style="margin: 0 0 20px 0; color: #7B2183; font-size: 16px; font-weight: bold;">
              Pertanyaan ${index + 1}: ${qa.questionTitle}
            </h5>
            
            <div style="margin-bottom: 20px; padding: 15px; background: white; border-radius: 6px; border: 1px solid #ddd;">
              <div style="display: flex; gap: 20px; flex-wrap: wrap;">
                <div style="flex: 1; min-width: 150px;">
                  <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Rata-rata Keseluruhan</div>
                  <div style="font-size: 24px; font-weight: bold; color: #7B2183;">${overallAverage.toFixed(2)}</div>
                </div>
                <div style="flex: 1; min-width: 150px;">
                  <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Total Respondent</div>
                  <div style="font-size: 24px; font-weight: bold; color: #333;">${totalResponses}</div>
                </div>
                <div style="flex: 1; min-width: 150px;">
                  <div style="font-size: 12px; color: #666; margin-bottom: 5px;">Tipe Respondent</div>
                  <div style="font-size: 16px; font-weight: bold; color: #333;">${roleTypes.join(', ')}</div>
                </div>
              </div>
            </div>

            <h6 style="margin: 20px 0 10px 0; color: #555; font-size: 14px;">Nilai rata-rata pertipe:</h6>
            <table border="1" cellpadding="10" cellspacing="0" style="width: 100%; margin-bottom: 20px; border-collapse: collapse;">
              <thead>
                <tr style="background-color: #f0f0f0;">
                  <th style="border: 1px solid #000; padding: 8px;">Tipe</th>
                  <th style="border: 1px solid #000; padding: 8px;">Rata-rata</th>
                  <th style="border: 1px solid #000; padding: 8px;">Jumlah Responden</th>
                </tr>
              </thead>
              <tbody>
        `;

        roleTypes.forEach((type) => {
          const typeData = qa.respondentTypeAverages[type];
          html += `
                <tr>
                  <td style="border: 1px solid #000; padding: 8px; font-weight: bold;">${type}</td>
                  <td style="border: 1px solid #000; padding: 8px; text-align: center;">${typeData.average.toFixed(2)}</td>
                  <td style="border: 1px solid #000; padding: 8px; text-align: center;">${typeData.count}</td>
                </tr>
          `;
        });

        html += `
              </tbody>
            </table>

            <h6 style="margin: 20px 0 10px 0; color: #555; font-size: 14px;">Distribution Chart:</h6>
            ${generateCombinedBarChart(qa.answerDistribution, qa.rangeItems)}
          </div>
        `;
      });

      html += '</div>';
    }

    return html;
  };

  // Generate category-based charts - SEPARATED BY RESPONDENT TYPE
  const generateCategoryChartsByType = () => {
    if (!data.categoryAverages || data.categoryAverages.length === 0) {
      return '';
    }

    const respondentTypes = [
      'Participant',
      'Supervisor',
      'Peer',
      'Subordinate',
      'External Respondent'
    ];

    let html = `
      <div class="category-analysis-section" style="margin-top: 40px;">
        <h3 style="border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px;">
          Analisis Berdasarkan Kategori
        </h3>
    `;

    respondentTypes.forEach((respondentType) => {
      const hasData = data.categoryAverages?.some(
        (ca) => ca.respondentTypeAverages[respondentType]
      );

      if (!hasData) return;

      html += `
        <div class="category-respondent-section" style="margin-bottom: 35px;">
          <h4 style="color: #333; margin-bottom: 15px; font-size: 16px;">
            ${respondentType} - Rata-rata per Kategori
          </h4>
          
          <table border="1" cellpadding="10" cellspacing="0" style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
            <thead>
              <tr style="background-color: #f5f5f5;">
                <th style="text-align: left; border: 1px solid #ddd; padding: 12px; font-weight: bold;">Kategori</th>
                <th style="text-align: center; border: 1px solid #ddd; padding: 12px; font-weight: bold; width: 150px;">Rata-rata Nilai</th>
                <th style="text-align: center; border: 1px solid #ddd; padding: 12px; font-weight: bold; width: 150px;">Jumlah Responden</th>
              </tr>
            </thead>
            <tbody>
      `;

      data.categoryAverages?.forEach((ca) => {
        const typeData = ca.respondentTypeAverages[respondentType];
        if (!typeData) return;

        html += `
              <tr>
                <td style="border: 1px solid #ddd; padding: 10px;">${ca.category}</td>
                <td style="border: 1px solid #ddd; padding: 10px; text-align: center; font-weight: bold;">${typeData.average.toFixed(2)}</td>
                <td style="border: 1px solid #ddd; padding: 10px; text-align: center;">${typeData.count}</td>
              </tr>
        `;
      });

      html += `
            </tbody>
          </table>
        </div>
      `;
    });

    html += '</div>';
    return html;
  };

  const businessUnitRows =
    data.businessUnits
      ?.map(
        (bu) => `
    <tr>
      <td>${bu.name}</td>
      <td>${bu.participantsCount}</td>
      <td>${bu.supervisorsCount || ''}</td>
    </tr>
  `
      )
      .join('') ||
    `
    <tr>
      <td>No business unit data available</td>
      <td>0</td>
      <td>0</td>
    </tr>`;

  const totalParticipants =
    data.businessUnits?.reduce((sum, bu) => sum + bu.participantsCount, 0) ||
    data.participantsRespondedCount ||
    0;
  const totalSupervisors =
    data.businessUnits?.reduce((sum, bu) => sum + bu.supervisorsCount, 0) ||
    data.supervisorsRespondedCount ||
    0;

  // Generate dynamic info table rows based on available respondent types
  let infoTableRespondentRows = '';

  if (availableTypes.includes('Participant')) {
    infoTableRespondentRows += `
        <tr>
          <td>Jumlah Peserta yang mengisi</td>
          <td>: ${data.participantsRespondedCount} people out of ${data.participantsCount} (${data.participantsCount > 0 ? Math.round((data.participantsRespondedCount / data.participantsCount) * 100) : 0}%)</td>
        </tr>`;
  }

  if (availableTypes.includes('Supervisor')) {
    infoTableRespondentRows += `
        <tr>
          <td>Jumlah Atasan yang mengisi</td>
          <td>: ${data.supervisorsRespondedCount} people out of ${data.totalSupervisors} (${data.totalSupervisors > 0 ? Math.round((data.supervisorsRespondedCount / data.totalSupervisors) * 100) : 0}%)</td>
        </tr>`;
  }

  if (availableTypes.includes('Peer')) {
    infoTableRespondentRows += `
        <tr>
          <td>Jumlah peer yang mengisi assessment</td>
          <td>: ${data.peersRespondedCount} out of ${data.totalPeers} (${data.totalPeers > 0 ? Math.round((data.peersRespondedCount / data.totalPeers) * 100) : 0}%)</td>
        </tr>`;
  }

  if (availableTypes.includes('Subordinate')) {
    infoTableRespondentRows += `
        <tr>
          <td>Jumlah subordinate yang mengisi assessment</td>
          <td>: ${data.subordinatesRespondedCount} out of ${data.totalSubordinates} (${data.totalSubordinates > 0 ? Math.round((data.subordinatesRespondedCount / data.totalSubordinates) * 100) : 0}%)</td>
        </tr>`;
  }

  if (availableTypes.includes('External Respondent')) {
    infoTableRespondentRows += `
        <tr>
          <td>Jumlah external respondent yang mengisi assessment</td>
          <td>: ${data.externalRespondentsRespondedCount} out of ${data.totalExternalRespondents} (${data.totalExternalRespondents > 0 ? Math.round((data.externalRespondentsRespondedCount / data.totalExternalRespondents) * 100) : 0}%)</td>
        </tr>`;
  }

  return `
    <div class="lia-report">
      <div class="report-header">
        <h1>Learning Impact Assessment (LIA)</h1>
      </div>

      <table class="info-table">
        <tr>
          <td width="30%">Program</td>
          <td width="70%">: ${data.programName || 'Program Name'}</td>
        </tr>
        <tr>
          <td>Periode Pelatihan</td>
          <td>: <span style="color: red;">(perlu diisi)</span></td>
        </tr>
        <tr>
          <td>Nama Modul</td>
          <td>: ${data.moduleName || 'Module Name'}</td>
        </tr>
        <tr>
          <td>Tanggal Pelatihan</td>
          <td>: <span style="color: red;">(perlu diisi)</span></td>
        </tr>
        <tr>
          <td>Jumlah Peserta</td>
          <td>: ${data.participantsCount} people</td>
        </tr>
        ${infoTableRespondentRows}
        ${
          data.modules && data.modules.length > 0
            ? data.modules
                .map(
                  (module, index) => `
        <tr>
          <td>${data.modules!.length > 1 ? `Tujuan Pembelajaran ${module.moduleName ? `(${module.moduleName})` : `Module ${index + 1}`}` : 'Tujuan Pembelajaran'}</td>
          <td>
            ${
              module.learningObjectives
                ? module.learningObjectives.replace(/\n/g, '<br>')
                : data.learningObjectives
                  ? data.learningObjectives.replace(/\n/g, '<br>')
                  : `
            1. Mengidentifikasi berbagai jenis AI di tempat kerja.<br>
            2. Menentukan alat/aplikasi AI untuk mendukung proses operasional.<br>
            3. Menggabungkan berbagai bentuk interaksi terstruktur dengan alat AI ke dalam konteks operasional.`
            }
          </td>
        </tr>
                `
                )
                .join('')
            : `
        <tr>
          <td>Tujuan Pembelajaran</td>
          <td>
            ${
              data.learningObjectives
                ? data.learningObjectives.replace(/\n/g, '<br>')
                : `
            1. Mengidentifikasi berbagai jenis AI di tempat kerja.<br>
            2. Menentukan alat/aplikasi AI untuk mendukung proses operasional.<br>
            3. Menggabungkan berbagai bentuk interaksi terstruktur dengan alat AI ke dalam konteks operasional.`
            }
          </td>
        </tr>
        `
        }
      </table>

      <h3>Nilai Rata-rata Pertanyaan</h3>
      <p>${averageDescriptionText}</p>
      
      <table class="performance-table">
        <tr>
          <th rowspan="2">Pertanyaan</th>
          <th colspan="${availableTypes.length + 1}">Nilai Avg</th>
        </tr>
        <tr>
          ${questionAveragesHeaders}
          <td><strong>Overall</strong></td>
        </tr>
        ${questionAveragesRows}
        ${questionAveragesFooter}
      </table>

      ${generateQuestionChartsByType()}

      ${generateCategoryChartsByType()}

      <h3>Distribusi Peserta Berdasarkan Business Unit</h3>
      <p>Pelatihan ini diikuti oleh berbagai <em>Business Unit</em> dari BHE maupun Division.</p>
      
      <p>Jumlah Peserta dan Atasan yang mengisi kuesioner (berdasarkan Bisnis Unit):</p>
      
      <table class="business-unit-table">
        <tr>
          <th><strong>Business Unit/Division</strong></th>
          <th><strong>Peserta</strong></th>
          <th><strong>Atasan</strong></th>
        </tr>
        ${businessUnitRows}
        <tr>
          <td><strong>Jumlah</strong></td>
          <td><strong>${totalParticipants}</strong></td>
          <td><strong>${totalSupervisors}</strong></td>
        </tr>
      </table>

      ${
        data.summaryDescription
          ? `
      <div class="summary-report-section" style="margin-top: 40px; page-break-before: auto;">
        <h3>Ringkasan Laporan Assessment</h3>
        <p style="white-space: pre-wrap;">${data.summaryDescription}</p>
      </div>
      `
          : ''
      }
    </div>
  `;
};

export const generateLIAReportStyles = (): string => {
  return `
    .lia-report {
      font-family: 'Calibri', Arial, sans-serif;
      color: #333;
      line-height: 1.5;
      padding: 20px;
      max-width: 100%;
    }
    
    .report-header {
      text-align: center;
      margin-bottom: 20px;
    }
    
    .report-header h1 {
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 10px;
    }
    
    .language-note, .demographic-note, .clustering-note, .scale-note {
      color: #666;
      font-style: italic;
      background-color: #f8f9fa;
      padding: 2px 4px;
      border-radius: 2px;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
      border: 1px solid #000;
    }
    
    th, td {
      border: 1px solid #000;
      padding: 8px 10px;
      vertical-align: top;
    }
    
    th {
      background-color: #f5f5f5;
      font-weight: bold;
    }
    
    .info-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    
    .info-table td {
      border: 1px solid #000;
      padding: 8px 10px;
      vertical-align: top;
    }
    
    .info-table td:first-child {
      width: 30%;
      font-weight: normal;
    }
    
    .performance-table, .business-unit-table, .usage-table, .changes-table, .impact-table {
      width: 100%;
      border-collapse: collapse;
      margin: 15px 0 20px;
      border: 1px solid #000;
    }
    
    .performance-table th, 
    .business-unit-table th, 
    .usage-table th, 
    .changes-table th, 
    .impact-table th {
      background-color: #f5f5f5;
      border: 1px solid #000;
      padding: 8px;
      text-align: left;
      font-weight: bold;
    }
    
    .performance-table td, 
    .business-unit-table td, 
    .usage-table td, 
    .changes-table td, 
    .impact-table td {
      padding: 8px;
      border: 1px solid #000;
    }
    
    .performance-table tr:nth-child(even), 
    .business-unit-table tr:nth-child(even), 
    .usage-table tr:nth-child(even), 
    .changes-table tr:nth-child(even), 
    .impact-table tr:nth-child(even) {
      background-color: #f9f9f9;
    }
    
    .chart-placeholder {
      border: 2px dashed #aaa;
      background-color: #f9f9f9;
      padding: 30px;
      text-align: center;
      margin: 15px 0;
      color: #666;
      border-radius: 8px;
    }
    
    .chart-placeholder-title {
      font-weight: bold;
      font-size: 16px;
      color: #333;
      margin-bottom: 8px;
    }
    
    .chart-placeholder-description {
      font-style: italic;
      color: #666;
      font-size: 14px;
      line-height: 1.4;
    }
    
    .divider {
      border-bottom: 1px solid #ddd;
      padding-top: 20px;
      margin-bottom: 20px;
      text-align: center;
      color: #999;
      font-size: 0.9em;
    }
    
    h2 {
      font-size: 16px;
      font-weight: bold;
      margin-top: 25px;
      margin-bottom: 15px;
      color: #333;
    }
    
    h3 {
      font-size: 14px;
      font-weight: bold;
      margin-top: 20px;
      margin-bottom: 10px;
      color: #333;
    }
    
    strong {
      font-weight: bold;
    }
    
    .summary-report-section {
      margin-top: 40px;
      page-break-inside: avoid;
    }
    
    .summary-report-section h2 {
      border-bottom: 3px solid #007BFF;
      padding-bottom: 12px;
      margin-bottom: 20px;
      color: #007BFF;
      font-size: 18px;
    }
    
    .summary-report-section div {
      background-color: #f8f9fa;
      padding: 20px;
      border-radius: 8px;
      border-left: 4px solid #007BFF;
    }
    
    .summary-report-section p {
      line-height: 1.8;
      text-align: justify;
      white-space: pre-wrap;
      margin: 0;
    }
    
    @media print {
      .lia-report {
        font-size: 11pt;
        padding: 0;
      }
      
      table, th, td,
      .info-table td,
      .performance-table td, .performance-table th,
      .business-unit-table td, .business-unit-table th,
      .usage-table td, .usage-table th,
      .changes-table td, .changes-table th,
      .impact-table td, .impact-table th {
        border: 1pt solid #000 !important;
        -webkit-print-color-adjust: exact;
      }
      
      .chart-placeholder {
        border: 1pt dashed #aaa;
        padding: 20pt;
        margin: 10pt 0;
      }
      
      .page-break-before {
        page-break-before: always;
      }
    }
  `;
};

export const exportLIAReportToWord = async (
  reportHtml: string,
  fileName: string = 'lia_report.docx'
) => {
  const msoTableStyles = `
   <!--[if gte mso 9]>
   <xml>
     <w:WordDocument>
       <w:View>Print</w:View>
       <w:Zoom>90</w:Zoom>
       <w:DoNotOptimizeForBrowser/>
     </w:WordDocument>
   </xml>
   <![endif]-->
   <style>
     /* MSO specific table styles */
     table {
       border-collapse: collapse;
       width: 100%;
       mso-table-lspace: 0pt;
       mso-table-rspace: 0pt;
       border: 1pt solid black;
     }
     
     td, th {
       border: 1pt solid black;
       mso-border-alt: solid black .5pt;
       padding: 5pt;
     }
     
     th {
       mso-background-themecolor: text2;
       mso-background-themetint: 50;
       font-weight: bold;
     }
   </style>
 `;

  const fullHtml = `
   <html xmlns:o='urn:schemas-microsoft-com:office:office' 
         xmlns:w='urn:schemas-microsoft-com:office:word' 
         xmlns='http://www.w3.org/TR/REC-html40'>
     <head>
       <meta charset="utf-8">
       <title>Learning Impact Assessment Report</title>
       ${msoTableStyles}
       <style>
         ${generateLIAReportStyles()}
         
         /* Word-specific styles */
         @page {
           size: 21cm 29.7cm;
           margin: 2cm;
           mso-page-orientation: portrait;
         }
         
         body {
           font-family: 'Calibri', sans-serif;
         }
       </style>
     </head>
     <body>
       ${reportHtml}
     </body>
   </html>
 `;

  try {
    // Convert HTML to proper .docx format using html-docx-js
    const converted = await htmlDocx.asBlob(fullHtml);

    // Ensure it's a Blob (in browser environment it should be)
    let blob: Blob;
    if (converted instanceof Blob) {
      blob = converted;
    } else {
      // Handle Buffer type (shouldn't happen in browser, but for type safety)
      const arrayBuffer = converted.buffer.slice(
        converted.byteOffset,
        converted.byteOffset + converted.byteLength
      ) as ArrayBuffer;
      blob = new Blob([arrayBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
    }

    // Create a download link
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;

    // Trigger the download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(link.href);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error converting HTML to DOCX:', error);
    alert('Failed to export to Word. Please try again.');
  }
};

// Export report to PDF using browser print functionality
export const exportLIAReportToPDF = (reportHtml: string, _fileName: string = 'lia_report.pdf') => {
  // Create a new window for printing
  const printWindow = window.open('', '_blank');

  if (!printWindow) {
    alert('Please allow pop-ups to export PDF');
    return;
  }

  const fullHtml = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8">
        <title>Learning Impact Assessment Report</title>
        <style>
          ${generateLIAReportStyles()}
          
          /* PDF-specific styles */
          @media print {
            @page {
              size: A4;
              margin: 2cm;
            }
            
            body {
              font-family: 'Calibri', 'Arial', sans-serif;
              font-size: 11pt;
              line-height: 1.6;
              color: #000;
            }
            
            .page-break {
              page-break-after: always;
            }
            
            .no-break {
              page-break-inside: avoid;
            }
            
            /* Ensure tables don't break awkwardly */
            table {
              page-break-inside: avoid;
            }
            
            /* Hide interactive elements */
            button, .no-print {
              display: none !important;
            }
            
            /* Ensure charts display properly */
            .question-chart, .category-chart {
              page-break-inside: avoid;
              margin-bottom: 20px;
            }
            
            /* Better table borders for PDF */
            table {
              border: 1px solid #000 !important;
            }
            
            td, th {
              border: 1px solid #000 !important;
            }
          }
          
          /* Screen styles for preview */
          @media screen {
            body {
              max-width: 210mm;
              margin: 20px auto;
              padding: 20px;
              background: white;
              box-shadow: 0 0 10px rgba(0,0,0,0.1);
            }
          }
        </style>
      </head>
      <body>
        ${reportHtml}
      </body>
    </html>
  `;

  printWindow.document.write(fullHtml);
  printWindow.document.close();

  // Wait for content to load, then trigger print dialog
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      // Note: We don't auto-close the window to let user save the PDF
    }, 250);
  };
};

export default {
  prepareLIAReportData,
  generateLIAReportTemplate,
  generateLIAReportStyles,
  exportLIAReportToWord,
  exportLIAReportToPDF
};
